import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import RoomCard from "@/components/RoomCard";
import AdminEditRoomDialog from "@/components/AdminEditRoomDialog";
import { formatPrice } from "@/lib/currency";
import { Plus, Sparkles, MessageCircle, Home, Gift, Copy, Trash2, Pencil, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// Reusable animation preset — a soft fade-and-rise that keeps the app feeling
// quick (short duration, once-only) while giving each section a bit of life.
const revealProps = (delay = 0) => ({
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [rooms, setRooms] = useState([]);
    const [matches, setMatches] = useState([]);
    const [chains, setChains] = useState([]);
    const [buddies, setBuddies] = useState([]);
    const [threads, setThreads] = useState([]);
    const [referral, setReferral] = useState({ referral_code: "", referrals_count: 0 });
    const [deletingRoomId, setDeletingRoomId] = useState(null);
    const [editingRoom, setEditingRoom] = useState(null);

    const loadData = async () => {
        try {
            const [r, m, c, b, t, ref] = await Promise.all([
                api.get("/rooms/mine"),
                api.get("/matches"),
                api.get("/matches/chain").catch(() => ({ data: [] })),
                api.get("/matches/buddies").catch(() => ({ data: [] })),
                api.get("/messages/threads"),
                api.get("/referral"),
            ]);
            setRooms(r.data);
            setMatches(m.data);
            setChains(c.data || []);
            setBuddies(b.data || []);
            setThreads(t.data);
            setReferral(ref.data);
        } catch (err) {
            console.warn("dashboard load failed", err?.message);
        }
    };

    useEffect(() => { loadData(); }, []);

    const deleteRoom = async (roomId) => {
        setDeletingRoomId(roomId);
        try {
            await api.delete(`/rooms/${roomId}`);
            toast.success("Room removed");
            setRooms((prev) => prev.filter((r) => r.id !== roomId));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't remove room");
        } finally {
            setDeletingRoomId(null);
        }
    };

    // Total matches spans 1-on-1 swaps + 3-way chains + Exchange Buddies.
    const totalMatches = matches.length + chains.length + buddies.length;

    const stats = [
        { label: "My rooms", value: rooms.length, icon: Home, testId: "stat-my-rooms" },
        { label: "Matches", value: totalMatches, icon: Sparkles, testId: "stat-matches" },
        { label: "Chats", value: threads.length, icon: MessageCircle, testId: "stat-threads" },
    ];

    const shareUrl = `${window.location.origin}/register?ref=${referral.referral_code}`;
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Invite link copied!");
        } catch {
            toast.error("Couldn't copy. Select and copy manually");
        }
    };
    const shareNative = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "Join me on ErasmusSwap",
                    text: "Swap rooms with exchange students across the world.",
                    url: shareUrl,
                });
            } catch { /* user canceled */ }
        } else {
            copyLink();
        }
    };

    const firstName = user?.name?.split(" ")[0] || "there";

    return (
        <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 sm:py-10">
            {/* ── Hero ─────────────────────────────────────────────── */}
            <motion.section
                {...revealProps(0)}
                className="relative overflow-hidden rounded-3xl p-6 sm:p-8 text-white shadow-xl"
                style={{ background: "linear-gradient(135deg, #e88960 0%, #de7050 45%, #c85e40 100%)" }}
            >
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-white/20 blur-2xl" aria-hidden />
                <div className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" aria-hidden />
                <div className="relative">
                    <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/70">Dashboard</div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 leading-tight">
                        Hey {firstName} 👋
                    </h1>
                    <p className="text-white/80 mt-2 text-sm sm:text-base max-w-lg">
                        Your rooms, matches and conversations at a glance.
                    </p>

                    <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-3">
                        {stats.map((s, i) => (
                            <motion.div
                                key={s.label}
                                data-testid={s.testId}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.15 + i * 0.08, duration: 0.4, ease: "easeOut" }}
                                className="bg-white/15 backdrop-blur-md rounded-2xl px-3 py-3 sm:p-4 border border-white/15"
                            >
                                <s.icon className="w-4 h-4 text-white/80" />
                                <div className="font-display text-2xl sm:text-3xl font-bold mt-1 tabular-nums">
                                    {s.value}
                                </div>
                                <div className="text-[10px] sm:text-xs text-white/70 font-medium mt-0.5 truncate">
                                    {s.label}
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-5 flex flex-wrap gap-2">
                        <Button
                            data-testid="dashboard-add-room-btn"
                            onClick={() => navigate("/listings/new")}
                            className="rounded-full h-11 bg-white text-primary hover:bg-white/90 font-semibold shadow-sm"
                        >
                            <Plus className="w-4 h-4 mr-1" /> New room
                        </Button>
                        <Button
                            data-testid="dashboard-edit-profile-btn"
                            onClick={() => navigate("/profile")}
                            variant="outline"
                            className="rounded-full h-11 border-white/40 text-white hover:bg-white/10 hover:text-white bg-transparent"
                        >
                            Edit profile
                        </Button>
                    </div>
                </div>
            </motion.section>

            {/* ── My rooms (above Invite Friends per user request) ─── */}
            <motion.section {...revealProps(0.05)} className="mt-10">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl font-semibold">My rooms</h2>
                    <Link to="/listings/new" className="text-sm text-primary font-semibold hover:underline inline-flex items-center gap-1" data-testid="dashboard-new-link">
                        <Plus className="w-4 h-4" /> New
                    </Link>
                </div>
                {rooms.length === 0 ? (
                    <div className="mt-4 border border-dashed border-border rounded-2xl p-10 text-center bg-white/40" data-testid="empty-rooms">
                        <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
                            <Home className="w-7 h-7 text-primary" />
                        </div>
                        <p className="mt-4 text-muted-foreground max-w-sm mx-auto">
                            You haven't listed any rooms yet. Add your first room to find matches.
                        </p>
                        <Button
                            data-testid="empty-rooms-cta"
                            onClick={() => navigate("/listings/new")}
                            className="mt-4 rounded-full bg-primary hover:bg-primary/90"
                        >
                            List a room
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                        {rooms.map((r, i) => (
                            <motion.div
                                key={r.id}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ delay: i * 0.05, duration: 0.4, ease: "easeOut" }}
                                className="relative group"
                            >
                                <RoomCard room={r} />
                                <button
                                    type="button"
                                    data-testid={`edit-room-btn-${r.id}`}
                                    className="absolute top-3 right-14 z-10 w-9 h-9 rounded-full bg-white/95 border border-border hover:bg-primary hover:text-white hover:border-primary transition-colors flex items-center justify-center shadow-sm"
                                    title="Edit this listing"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingRoom(r); }}
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <button
                                            type="button"
                                            disabled={deletingRoomId === r.id}
                                            data-testid={`delete-room-btn-${r.id}`}
                                            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors flex items-center justify-center shadow-sm"
                                            title="Remove this listing"
                                            onClick={(e) => { e.stopPropagation(); }}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent data-testid={`delete-room-dialog-${r.id}`}>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Remove this room?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                "{r.title}" will be removed from the platform. Matches and conversations for this room will stay, but no one will be able to find the listing anymore. You can always list a new one later.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel data-testid={`delete-room-cancel-${r.id}`}>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                data-testid={`delete-room-confirm-${r.id}`}
                                                onClick={() => deleteRoom(r.id)}
                                                className="bg-destructive hover:bg-destructive/90"
                                            >
                                                Yes, remove it
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </motion.div>
                        ))}
                    </div>
                )}
            </motion.section>

            {/* ── Invite friends ────────────────────────────────────── */}
            <motion.section {...revealProps(0.1)} className="mt-12">
                <div className="relative overflow-hidden bg-white border border-border/60 rounded-3xl p-6 sm:p-8">
                    <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-accent/30 blur-2xl" aria-hidden />
                    <div className="relative grid md:grid-cols-2 gap-6 items-center min-w-0">
                        <div className="min-w-0">
                            <div className="inline-flex items-center gap-2 bg-accent/40 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
                                <Gift className="w-3 h-3" />
                                Grow the community
                            </div>
                            <h2 className="font-display text-2xl font-bold mt-3">Invite friends</h2>
                            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
                                The more students on ErasmusSwap, the better the matches for everyone. Share your personal link.
                            </p>
                        </div>
                        <div className="bg-muted rounded-2xl p-5 min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Your invite link</div>
                            <div className="mt-2 bg-white border border-border rounded-xl p-2.5 flex items-center gap-2 min-w-0">
                                <div className="font-mono text-xs sm:text-sm truncate flex-1 min-w-0" data-testid="referral-link">{shareUrl}</div>
                                <Button size="sm" variant="ghost" onClick={copyLink} data-testid="referral-copy-btn" className="rounded-full shrink-0 h-8 w-8 p-0">
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                            <Button onClick={shareNative} data-testid="referral-share-btn" className="w-full mt-3 rounded-full h-11 bg-foreground text-background hover:bg-foreground/90">
                                Share invite link
                            </Button>
                            <div className="text-xs text-muted-foreground mt-3" data-testid="referrals-count">
                                {referral.referrals_count} friend{referral.referrals_count === 1 ? "" : "s"} joined so far
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ── Match suggestions ─────────────────────────────────── */}
            <motion.section {...revealProps(0.15)} className="mt-12">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl font-semibold">Match suggestions</h2>
                    {matches.length > 0 && (
                        <Link to="/matches" className="text-sm text-primary font-semibold hover:underline inline-flex items-center gap-1">
                            See all <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    )}
                </div>
                {matches.length === 0 ? (
                    <p className="mt-3 text-muted-foreground text-sm" data-testid="no-matches-text">
                        No matches yet. Add a room with the city you're heading to and we'll find candidates instantly.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        {matches.slice(0, 4).map((m, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ delay: i * 0.06, duration: 0.4, ease: "easeOut" }}
                            >
                                <Link
                                    to="/matches"
                                    data-testid={`match-preview-${i}`}
                                    className="bg-white border border-border/60 rounded-2xl p-5 hover:-translate-y-0.5 transition-all duration-300 hover:shadow-md block"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                                            Match · {m.score}%
                                        </div>
                                        <div className="text-sm font-semibold">
                                            {(() => {
                                                const cur = (m.price_difference >= 0 ? m.their_room?.currency : m.my_room?.currency) || "EUR";
                                                const sign = m.price_difference >= 0 ? "+" : "-";
                                                return `${sign}${formatPrice(Math.abs(m.price_difference), cur)}`;
                                            })()}
                                        </div>
                                    </div>
                                    <div className="mt-3 font-display text-lg font-semibold">
                                        {m.my_room.city} ↔ {m.their_room.city}
                                    </div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                        with {m.their_room.owner_name}
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}
            </motion.section>

            {editingRoom && (
                <AdminEditRoomDialog
                    room={editingRoom}
                    open={!!editingRoom}
                    onOpenChange={(o) => { if (!o) setEditingRoom(null); }}
                    apiPath={`/rooms/${editingRoom.id}`}
                    method="put"
                    onSaved={(updated) => {
                        setRooms((prev) => prev.map((x) => x.id === updated.id ? updated : x));
                        setEditingRoom(null);
                    }}
                />
            )}
        </div>
    );
}
